import React, { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface SectionProps {
  id?: string;
  children: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

export default function Section({
  id,
  children,
  title,
  description,
  className,
}: SectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const header = headerRef.current;
    const content = contentRef.current;
    const section = sectionRef.current;

    if (!header || !content || !section) return;

    const ctx = gsap.context(() => {
      // Animation du header (titre + description)
      const headerTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: header,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });

      headerTimeline.fromTo(
        header,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" }
      );

      // Animation du contenu
      const contentTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: content,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });

      contentTimeline.fromTo(
        content,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, delay: 0.15, ease: "power2.out" }
      );

      // Parallax très léger sur toute la section
      gsap.to(section, {
        y: -15,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={sectionRef}
      id={id}
      className={cn("md:py-20 py-8 px-8 xl:px-0", className)}
    >
      <div className="max-w-6xl mx-auto flex flex-col gap-10">
        <div
          ref={headerRef}
          className="flex lg:flex-row flex-col gap-4 justify-between"
        >
          <h2 className="md:text-5xl text-4xl flex-1 text-balance leading-tight">
            {title}
          </h2>
          <p className="flex-1 text-slate-700 text-base">{description}</p>
        </div>
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
}
